import React, { createContext, useState, useEffect, useContext, ReactNode, useLayoutEffect } from 'react';

type Theme = 'light' | 'dark';

interface ThemeContextType {
  theme: Theme;
  isDarkMode: boolean;
  toggleTheme: () => void;
  setDarkMode: (isDark: boolean) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState<boolean>(() => {
    // Check localStorage first
    const savedTheme = localStorage.getItem('app-theme');
    const themeMode = localStorage.getItem('theme-preference-mode');
    
    // If user manually set a theme, use that
    if (savedTheme && themeMode === 'manual') {
      return savedTheme === 'dark';
    }
    
    // Otherwise, use system preference as default
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    const systemTheme = prefersDark ? 'dark' : 'light';
    
    // Save the system theme as default
    localStorage.setItem('app-theme', systemTheme);
    localStorage.setItem('theme-preference-mode', 'system');
    
    return prefersDark;
  });

  // IMPORTANT: Use useLayoutEffect instead of useEffect to apply theme BEFORE browser paint
  useLayoutEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    
    // Also store in localStorage
    localStorage.setItem('app-theme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  // Listen for system theme changes ONLY if user hasn't manually overridden
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleSystemThemeChange = (e: MediaQueryListEvent) => {
      const themeMode = localStorage.getItem('theme-preference-mode');
      
      // Only update if user is using system theme
      if (themeMode === 'system') {
        const newIsDark = e.matches;
        setIsDarkMode(newIsDark);
        
        // Apply immediately without waiting for re-render
        if (newIsDark) {
          document.documentElement.classList.add('dark');
        } else {
          document.documentElement.classList.remove('dark');
        }
      }
    };

    mediaQuery.addEventListener('change', handleSystemThemeChange);
    return () => mediaQuery.removeEventListener('change', handleSystemThemeChange);
  }, []);

  const toggleTheme = () => {
    setIsDarkMode(prev => {
      const newValue = !prev;
      
      // Save manual preference
      localStorage.setItem('app-theme', newValue ? 'dark' : 'light');
      localStorage.setItem('theme-preference-mode', 'manual');
      
      // Apply immediately
      if (newValue) {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
      
      return newValue;
    });
  };

  const setDarkMode = (isDark: boolean) => {
    setIsDarkMode(isDark);
    
    // Save manual preference
    localStorage.setItem('app-theme', isDark ? 'dark' : 'light');
    localStorage.setItem('theme-preference-mode', 'manual');
    
    // Apply immediately
    if (isDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  };

  return (
    <ThemeContext.Provider value={{ 
      theme: isDarkMode ? 'dark' : 'light', 
      isDarkMode, 
      toggleTheme, 
      setDarkMode 
    }}>
      {children}
    </ThemeContext.Provider>
  );
};

// Custom hook for using the theme context
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};